'use client';

import type { DiagnosticResults } from './ResultsInteractive';

interface ProfileConfig {
  label: string;
  description: string;
  detail: string;
  color: string;
  borderColor: string;
  bgColor: string;
  urgencyLabel: string;
  urgencyBg: string;
  urgencyColor: string;
  barColor: string;
}

const PROFILE_CONFIG: Record<DiagnosticResults['profile'], ProfileConfig> = {
  stable: {
    label: 'Estrutura Globalmente Estável',
    description: 'A sua junta de freguesia apresenta uma organização operacional sólida e processos bem estabelecidos.',
    detail: 'Os resultados indicam que a sua estrutura administrativa funciona de forma eficiente. Recomendamos manutenção preventiva e monitorização contínua para preservar este nível de desempenho.',
    color: '#2E7D32',
    borderColor: 'rgba(46, 125, 50, 0.25)',
    bgColor: 'rgba(46, 125, 50, 0.04)',
    urgencyLabel: 'Baixa Urgência',
    urgencyBg: 'rgba(46, 125, 50, 0.1)',
    urgencyColor: '#2E7D32',
    barColor: '#2E7D32',
  },
  punctual: {
    label: 'Necessidades de Reforço Pontual',
    description: 'A sua junta apresenta uma base funcional adequada, mas com áreas específicas que beneficiariam de apoio técnico direcionado.',
    detail: 'Identificamos pontos de melhoria em áreas concretas que, com intervenção pontual, podem elevar significativamente a eficiência operacional da sua freguesia.',
    color: '#F57C00',
    borderColor: 'rgba(245, 124, 0, 0.25)',
    bgColor: 'rgba(245, 124, 0, 0.04)',
    urgencyLabel: 'Urgência Moderada',
    urgencyBg: 'rgba(245, 124, 0, 0.1)',
    urgencyColor: '#F57C00',
    barColor: '#F57C00',
  },
  support: {
    label: 'Necessidade Clara de Apoio Técnico',
    description: 'O diagnóstico revela lacunas técnicas significativas que requerem suporte especializado para garantir a conformidade e eficiência operacional.',
    detail: 'A sua junta enfrenta desafios técnicos em múltiplas áreas que, sem intervenção adequada, podem comprometer a qualidade dos serviços prestados aos munícipes e a conformidade legal.',
    color: '#E65100',
    borderColor: 'rgba(230, 81, 0, 0.25)',
    bgColor: 'rgba(230, 81, 0, 0.04)',
    urgencyLabel: 'Urgência Elevada',
    urgencyBg: 'rgba(230, 81, 0, 0.1)',
    urgencyColor: '#E65100',
    barColor: '#E65100',
  },
  pressure: {
    label: 'Pressão Operacional Elevada',
    description: 'O diagnóstico identifica pressão operacional crítica que exige intervenção técnica imediata e suporte especializado contínuo.',
    detail: 'A sua junta enfrenta desafios operacionais de alta complexidade que requerem atenção imediata. Recomendamos fortemente o início de um processo de apoio técnico estruturado o mais brevemente possível.',
    color: '#C62828',
    borderColor: 'rgba(198, 40, 40, 0.25)',
    bgColor: 'rgba(198, 40, 40, 0.04)',
    urgencyLabel: 'Urgência Crítica',
    urgencyBg: 'rgba(198, 40, 40, 0.1)',
    urgencyColor: '#C62828',
    barColor: '#C62828',
  },
};

const MAX_SCORE = 16;

interface ProfileCardProps {
  results: DiagnosticResults;
}

export default function ProfileCard({ results }: ProfileCardProps) {
  const config = PROFILE_CONFIG[results.profile];
  const scorePercent = Math.min(Math.round((results.totalScore / MAX_SCORE) * 100), 100);

  return (
    <div
      className="bg-white p-7 lg:p-8"
      style={{
        borderRadius: '5px',
        border: `2px solid ${config.borderColor}`,
        backgroundColor: config.bgColor,
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
      }}
    >
      <div className="flex flex-col sm:flex-row sm:items-start gap-5">
        <div
          className="flex-shrink-0 w-12 h-12 flex items-center justify-center"
          style={{ borderRadius: '5px', border: `1px solid ${config.borderColor}`, backgroundColor: 'rgba(255,255,255,0.8)' }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={config.color} strokeWidth="2" aria-hidden="true">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2.5 mb-3">
            <span
              className="inline-flex items-center px-2.5 py-1 text-xs font-semibold"
              style={{
                fontFamily: 'DM Sans, sans-serif',
                borderRadius: '5px',
                backgroundColor: config.urgencyBg,
                color: config.urgencyColor,
              }}
            >
              {config.urgencyLabel}
            </span>
            <span className="text-gray-400 text-xs" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              Pontuação:{' '}
              <span className="font-semibold text-gray-700" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {results.totalScore}/{MAX_SCORE}
              </span>
            </span>
          </div>
          <h2
            className="mb-2 font-bold"
            style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.4rem', lineHeight: '1.25', color: config.color }}
          >
            {config.label}
          </h2>
          <p
            className="text-gray-700 text-sm font-medium mb-3 leading-relaxed"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            {config.description}
          </p>
          <p
            className="text-gray-500 text-sm leading-relaxed"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            {config.detail}
          </p>
        </div>
      </div>

      {/* Score bar */}
      <div className="mt-7">
        <div className="flex items-center justify-between mb-2">
          <span className="text-gray-400 text-xs" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            Índice de Pressão Operacional
          </span>
          <span className="font-semibold text-gray-700 text-xs" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {scorePercent}%
          </span>
        </div>
        <div
          className="h-2 bg-white overflow-hidden"
          style={{ borderRadius: '0', border: '1px solid #E0E0E0' }}
        >
          <div
            className="h-full transition-all duration-700"
            style={{ width: `${scorePercent}%`, backgroundColor: config.barColor, borderRadius: '0' }}
            role="progressbar"
            aria-valuenow={scorePercent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Índice de pressão operacional: ${scorePercent}%`}
          />
        </div>
        <div className="flex justify-between mt-1.5">
          <span className="text-gray-400 text-xs" style={{ fontFamily: 'DM Sans, sans-serif' }}>Estável</span>
          <span className="text-gray-400 text-xs" style={{ fontFamily: 'DM Sans, sans-serif' }}>Pressão Crítica</span>
        </div>
      </div>
    </div>
  );
}